import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Tabs,
    Tab,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Radio,
    Tooltip,
    Modal,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { axios_tenant } from "../../../axios";
import { toast } from "react-toastify";
import Export from "./Export"; // Assuming you have an Export component for the export tab

function a11yProps(index) {
    return {
        id: `admin-tab-${index}`,
        "aria-controls": `admin-tabpanel-${index}`,
    };
}

export default function Admin() {
    const [tab, setTab] = useState(0);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [openInvite, setOpenInvite] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, user: null });
    const [openEdit, setOpenEdit] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editFirstName, setEditFirstName] = useState("");
    const [editLastName, setEditLastName] = useState("");

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sorting state
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

    // Invite modal state
    const [inviteFirstName, setInviteFirstName] = useState("");
    const [inviteLastName, setInviteLastName] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("user");


    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await axios_tenant.get("/user/fetchUserData");
            setUsers(res.data.data.userResults || []);
        } catch (e) {
            toast.error("Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (user, newRole) => {
        setActionLoading((prev) => ({ ...prev, [user.mail]: true }));
        try {
            await axios_tenant.post(`/user/editUserData/${user.personid}`, {
                userData: {
                    ...user,
                    role: newRole,
                }
            });
            toast.success("Role updated");
            fetchUsers();
        } catch (e) {
            toast.error("Failed to update role");
        } finally {
            setActionLoading((prev) => ({ ...prev, [user.mail]: false }));
        }
    };


    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Add User (Invite) Modal handlers
    const handleOpenInvite = () => {
        setInviteFirstName("");
        setInviteLastName("");
        setInviteEmail("");
        setInviteRole("user");
        setOpenInvite(true);
    };

    const handleCloseInvite = () => {
        setOpenInvite(false);
    };

    const handleInviteSubmit = async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        try {
            const res = await axios_tenant.post("/user/sendInvitation", {
                email: inviteEmail,
                role: inviteRole,
                firstName: inviteFirstName,
                lastName: inviteLastName,
            });
            if (res.data?.message === "User already exists") {
                toast.info("A user with this email is already added.");
            } else {
                toast.success(`User ${inviteFirstName} ${inviteLastName} Added Successfully`);
                setOpenInvite(false);
                fetchUsers();
            }
        } catch (err) {
            toast.error("Failed to send invite");
        } finally {
            setInviteLoading(false);
        }
    };

    const handleDeleteClick = (user) => {
        setConfirmDelete({ open: true, user });
    };

    const handleDeleteConfirm = async () => {
        const user = confirmDelete.user;
        setActionLoading((prev) => ({ ...prev, [user.mail]: true }));
        try {
            await axios_tenant.delete(`/user/delete/${user.personid}`, {
                email: user.mail,
            });
            toast.success("User deleted");
            fetchUsers();
        } catch (e) {
            toast.error("Failed to deactivate user");
        } finally {
            setActionLoading((prev) => ({ ...prev, [user.mail]: false }));
            setConfirmDelete({ open: false, user: null });
        }
    };

    const handleDeleteCancel = () => {
        setConfirmDelete({ open: false, user: null });
    };

    // Edit User Modal handlers
    const handleEditClick = (user) => {
        setEditUser(user);
        setEditFirstName(user.first_name);
        setEditLastName(user.last_name);
        setOpenEdit(true);
    };

    const handleEditCancel = () => {
        setOpenEdit(false);
        setEditUser(null);
        setEditFirstName("");
        setEditLastName("");
    };

    const handleEditSubmit = async () => {
        if (!editUser) return;
        
        setActionLoading((prev) => ({ ...prev, [editUser.mail]: true }));
        try {
            const response = await axios_tenant.post(`/user/editUserData/${editUser.personid}`, {
                userData: {
                    firstName: editFirstName,
                    lastName: editLastName,
                    email: editUser.mail, // Email should not be changed
                    role: editUser.role, // Keep the existing role
                    status: editUser.status, // Keep the existing status
                    apps: editUser.apps || [], // Keep existing apps if any
                }
            });
            toast.success("User updated successfully");
            fetchUsers();
            handleEditCancel();
        } catch (e) {
            toast.error("Failed to update user");
        } finally {
            setActionLoading((prev) => ({ ...prev, [editUser.mail]: false }));
        }
    };

    // Sorting function
    const handleSort = () => {
        const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newOrder);
        setPage(0); // Reset to first page when sorting
    };

    // Sort users by last name
    const sortUsers = (users) => {
        return [...users].sort((a, b) => {
            const lastNameA = a.last_name?.toLowerCase() || '';
            const lastNameB = b.last_name?.toLowerCase() || '';
            
            if (sortOrder === 'asc') {
                return lastNameA.localeCompare(lastNameB);
            } else {
                return lastNameB.localeCompare(lastNameA);
            }
        });
    };

    // Filter and paginate users
    const activeUsers = users.filter(user => user.status === "active");
    const sortedUsers = sortUsers(activeUsers);
    const paginatedUsers = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const isAdmin = localStorage.getItem("TENANT_ROLE") == "admin";

    if (!isAdmin) {
        return <Typography>You do not have access to this page.</Typography>;
    }

    return (
        <Box sx={{ 
            maxWidth: 1200, 
            mx: "auto"
            }}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 600 }}>
                Admin Panel
            </Typography>
            <Paper sx={{
                p: 2,
                borderRadius: 3,
                background: "#fafdff",
                boxShadow: "0 2px 8px 0 rgba(60,72,100,0.07)"
            }}>
                <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label={<Typography sx={{ fontWeight: tab === 0 ? 600 : 400, color: tab === 0 ? "#1565c0" : "#b0b7c3" }}>Users</Typography>} {...a11yProps(0)} />
                    <Tab label={<Typography sx={{ fontWeight: tab === 1 ? 600 : 400, color: tab === 1 ? "#1565c0" : "#b0b7c3" }}>Export</Typography>} {...a11yProps(1)} />
                </Tabs>
                {tab === 0 && (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h3" gutterBottom >
                                Users List
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{ borderRadius: 2, fontWeight: 500 }}
                                onClick={handleOpenInvite}
                            >
                                Add User
                            </Button>
                        </Box>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <TableContainer component={Paper} sx={{
                                    boxShadow: "none",
                                    borderRadius: 2,
                                    background: "#fff",
                                    border: "1px solid #e0e3e7",
                                    '&, & *': {
                                        // fontSize: '1.05rem',
                                        // lineHeight: 1.5
                                    },
                                }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ background: "#f4f8fb" }}>
                                                <TableCell sx={{ fontWeight: 600, color: "#1a237e" }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        User Name
                                                        <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSort}
                                                                sx={{
                                                                    p: 0.5,
                                                                    color: '#1565c0',
                                                                    '&:hover': { backgroundColor: 'rgba(21, 101, 192, 0.1)' },
                                                                    transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                    transition: 'transform 0.2s ease'
                                                                }}
                                                            >
                                                                <SwapVertIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                                    Read/Write
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                                    Admin
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, color: "#1a237e" }}>
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody
                                            sx={{
                                                '& .MuiTableRow-root': {
                                                    height: 36, // Decrease row height
                                                },
                                                '& .MuiTableCell-root': {
                                                    py: 0.5, // Decrease vertical padding
                                                },
                                            }}
                                        >
                                            {paginatedUsers.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        No users found.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                paginatedUsers.map((user) => (
                                                    <TableRow key={user.mail}>
                                                        <TableCell sx={{ fontSize: 16 }}>
                                                            <Tooltip title={user.mail} placement="right">
                                                                <span>{user.last_name + ", " + user.first_name}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Radio
                                                                checked={user.role === "user"}
                                                                onChange={() => handleRoleChange(user, "user")}
                                                                disabled={actionLoading[user.mail]}
                                                                color="primary"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Radio
                                                                checked={user.role === "admin"}
                                                                onChange={() => handleRoleChange(user, "admin")}
                                                                disabled={actionLoading[user.mail]}
                                                                color="primary"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Delete">
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => handleDeleteClick(user)}
                                                                        disabled={actionLoading[user.mail]}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Edit">
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => handleEditClick(user)}
                                                                        disabled={actionLoading[user.mail]}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={sortedUsers.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 20, 50, 100]}
                                    sx={{
                                        '.MuiTablePagination-toolbar': {
                                            flexWrap: 'nowrap',
                                            gap: 1,
                                            minHeight: 56,
                                        },
                                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                            margin: 0,
                                        }
                                    }}
                                />
                            </>
                        )}
                        {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                            <Button variant="contained" color="primary" sx={{ minWidth: 80 }}>OK</Button>
                        </Box> */}
                        {/* Invite User Modal */}
                        <Modal
                            open={openInvite}
                            onClose={handleCloseInvite}
                            aria-labelledby="invite-user-modal-title"
                            aria-describedby="invite-user-modal-description"
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    bgcolor: "background.paper",
                                    border: "2px solid #000",
                                    boxShadow: 24,
                                    p: 4,
                                    maxHeight: "80vh",
                                    width: { xs: "90%", sm: 400 },
                                    overflowY: "auto",
                                }}
                                component="form"
                                onSubmit={handleInviteSubmit}
                            >
                                <Typography id="invite-user-modal-title" variant="h4" sx={{ mb: 2 }}>
                                    Add User
                                </Typography>
                                <TextField
                                    label="First Name"
                                    value={inviteFirstName}
                                    onChange={e => setInviteFirstName(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    required
                                />
                                <TextField
                                    label="Last Name"
                                    value={inviteLastName}
                                    onChange={e => setInviteLastName(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    required
                                />
                                <TextField
                                    label="Email"
                                    value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    required
                                    type="email"
                                />
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={inviteRole}
                                        label="Role"
                                        onChange={e => setInviteRole(e.target.value)}
                                    >
                                        <MenuItem value="user">Read/Write</MenuItem>
                                        <MenuItem value="admin">Admin</MenuItem>
                                    </Select>
                                </FormControl>
                                <Box textAlign="right">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={
                                            inviteLoading ||
                                            !inviteFirstName ||
                                            !inviteLastName ||
                                            !inviteEmail ||
                                            !inviteRole
                                        }
                                    >
                                        {inviteLoading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                        {/* Confirm Delete Modal */}
                        <Modal
                            open={confirmDelete.open}
                            onClose={handleDeleteCancel}
                            aria-labelledby="confirm-delete-title"
                            aria-describedby="confirm-delete-description"
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    bgcolor: "#fff",
                                    borderRadius: 2,
                                    boxShadow: 24,
                                    p: { xs: 4, sm: 6 }, // Increased padding
                                    minWidth: { xs: 280, sm: 420 },
                                    textAlign: "center"
                                }}
                            >
                                <Typography id="confirm-delete-description" sx={{ mb: 4, fontSize: 20 }}>
                                    Are you sure you want to delete this user?
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleDeleteCancel}
                                    >
                                        No
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleDeleteConfirm}
                                        disabled={actionLoading[confirmDelete.user?.mail]}
                                    >
                                        {actionLoading[confirmDelete.user?.mail] ? <CircularProgress size={22} color="inherit" /> : "Yes"}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                        
                        {/* Edit User Modal */}
                        <Modal
                            open={openEdit}
                            onClose={handleEditCancel}
                            aria-labelledby="edit-user-modal-title"
                            aria-describedby="edit-user-modal-description"
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    bgcolor: "background.paper",
                                    border: "2px solid #000",
                                    boxShadow: 24,
                                    p: 4,
                                    width: { xs: "90%", sm: 400 },
                                }}
                            >
                                <Typography id="edit-user-modal-title" variant="h4" sx={{ mb: 2 }}>
                                    Edit User
                                </Typography>
                                <TextField
                                    label="First Name"
                                    value={editFirstName}
                                    onChange={(e) => setEditFirstName(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    required
                                />
                                <TextField
                                    label="Last Name"
                                    value={editLastName}
                                    onChange={(e) => setEditLastName(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    required
                                />
                                <TextField
                                    label="Email"
                                    value={editUser?.mail || ""}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    disabled
                                />
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleEditCancel}
                                        // sx={{
                                        //     borderRadius: 2,
                                        //     fontWeight: 500,
                                        //     borderColor: "#1976d2",
                                        //     color: "#1976d2",
                                        //     "&:hover": { borderColor: "#1565c0", color: "#1565c0" }
                                        // }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleEditSubmit}
                                        disabled={actionLoading[editUser?.mail] || !editFirstName || !editLastName}
                                        // sx={{
                                        //     borderRadius: 2,
                                        //     fontWeight: 500,
                                        //     background: "#1565c0"
                                        // }}
                                    >
                                        {actionLoading[editUser?.mail] ? <CircularProgress size={22} color="inherit" /> : "Save"}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                    </>
                )}
                {tab === 1 && (
                    <Export />
                )}
            </Paper>
        </Box>
    );
};